import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useIsAdmin } from "@/hooks/useAuth";
import { useSiteSettings, SITE_SETTINGS_QUERY_KEY, BRAND_COLORS, type SiteSettings } from "@/hooks/useSiteSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { isAdminAccessAllowed } from "@/lib/adminAccess";
import { getPendingReviews, moderateReview, type PublicReview } from "@/lib/reviews.functions";


export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin | Coyote Creek Hat Co." },
      { name: "description", content: "Coyote Creek Hat Co. internal admin dashboard." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/admin" }],
  }),
  component: AdminPage,
});

const NAV_LABELS = ["Hats", "Tops", "Custom Hats", "Heritage", "News", "The Standard", "Our Values", "Range Map", "FAQs", "Contact Us", "Become a Dealer", "Creator Creek"];

async function saveSetting(key: string, value: unknown) {
  const { error } = await supabase
    .from("site_settings")
    .upsert({ key, value: value as never }, { onConflict: "key" });
  if (error) throw error;
}

function AdminPage() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { data: settings, isLoading } = useSiteSettings();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SiteSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [hostChecked, setHostChecked] = useState(false);
  const [hostAllowed, setHostAllowed] = useState(false);

  useEffect(() => {
    setHostAllowed(isAdminAccessAllowed());
    setHostChecked(true);
  }, []);

  useEffect(() => {
    if (!hostChecked || !hostAllowed) return;
    if (!authLoading && !session) navigate({ to: "/login", replace: true });
  }, [hostChecked, hostAllowed, authLoading, session, navigate]);


  useEffect(() => { if (settings) setDraft(settings); }, [settings]);

  if (!hostChecked) return null;
  if (!hostAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-heading text-5xl text-primary">404</h1>
          <p className="mt-2 text-muted-foreground">Page not found.</p>
        </div>
      </div>
    );
  }
  if (authLoading || roleLoading || isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!session) return null;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-heading text-3xl text-primary">Not authorized</h1>
          <p className="mt-2 text-muted-foreground">Your account doesn't have admin access.</p>
          <Button className="mt-6" onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/login" }); }}>Sign out</Button>
        </div>
      </div>
    );
  }
  if (!draft) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveSetting("creator_creek_live", draft.creator_creek_live),
        saveSetting("announcement_enabled", draft.announcement_enabled),
        saveSetting("announcement_text", draft.announcement_text),
        saveSetting("announcements", draft.announcements),
        saveSetting("announcement_bg_color", draft.announcement_bg_color),
        saveSetting("announcement_text_color", draft.announcement_text_color),
        saveSetting("promo_buy2get1_enabled", draft.promo_buy2get1_enabled),
        saveSetting("nav_visibility", draft.nav_visibility),
        saveSetting("header_bg_color", draft.header_bg_color),
        saveSetting("header_text_color", draft.header_text_color),
        saveSetting("header_logo_color", draft.header_logo_color),
        saveSetting("promo_captcha_enabled", draft.promo_captcha_enabled),
        saveSetting("promo_captcha_code", draft.promo_captcha_code),
        saveSetting("promo_marquee_bg_color", draft.promo_marquee_bg_color),
        saveSetting("promo_marquee_text_color", draft.promo_marquee_text_color),
        saveSetting("promo_marquee_text", draft.promo_marquee_text),
        saveSetting("feature_marquee_bg_color", draft.feature_marquee_bg_color),
        saveSetting("feature_marquee_text_color", draft.feature_marquee_text_color),
      ]);
      queryClient.setQueryData(SITE_SETTINGS_QUERY_KEY, draft);
      await queryClient.invalidateQueries({ queryKey: SITE_SETTINGS_QUERY_KEY });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-6 pb-12">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl uppercase tracking-[0.15em] text-primary">Admin</h1>
            <p className="text-sm text-muted-foreground mt-1">{session.user.email}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted">View site</Link>
            <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/login" }); }}>Sign out</Button>
          </div>
        </div>

        <Tabs defaultValue="pages">
          <TabsList>
            <TabsTrigger value="pages">Pages</TabsTrigger>
            <TabsTrigger value="nav">Nav menu</TabsTrigger>
            <TabsTrigger value="header">Header</TabsTrigger>
            <TabsTrigger value="announcement">Announcement</TabsTrigger>
            <TabsTrigger value="promo">Promo</TabsTrigger>
            <TabsTrigger value="popup">Popup</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="pages" className="mt-6 space-y-4">
            <Row label="Creator Creek page live" description="When off, visitors see a 'Coming Soon' message. Hero stays visible.">
              <Switch checked={draft.creator_creek_live} onCheckedChange={(v) => setDraft({ ...draft, creator_creek_live: v })} />
            </Row>
          </TabsContent>

          <TabsContent value="nav" className="mt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Toggle which menu items appear in the header.</p>
            {NAV_LABELS.map((label) => (
              <Row key={label} label={label}>
                <Switch
                  checked={draft.nav_visibility[label] !== false}
                  onCheckedChange={(v) => setDraft({ ...draft, nav_visibility: { ...draft.nav_visibility, [label]: v } })}
                />
              </Row>
            ))}
          </TabsContent>

          <TabsContent value="header" className="mt-6 space-y-6">
            <p className="text-sm text-muted-foreground">Customize the header colors: background, menu text, and logo tint.</p>
            <ColorPicker
              label="Header background color"
              value={draft.header_bg_color}
              onChange={(v) => setDraft({ ...draft, header_bg_color: v })}
            />
            <ColorPicker
              label="Menu text color"
              value={draft.header_text_color}
              onChange={(v) => setDraft({ ...draft, header_text_color: v })}
            />
            <ColorPicker
              label="Logo color"
              value={draft.header_logo_color}
              onChange={(v) => setDraft({ ...draft, header_logo_color: v })}
            />
            <div className="rounded-lg border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview</div>
              <div
                className="rounded-md px-6 py-4 flex items-center justify-between"
                style={{ backgroundColor: draft.header_bg_color }}
              >
                <span className="font-heading text-lg font-black uppercase tracking-[0.15em]" style={{ color: draft.header_logo_color }}>
                  Coyote Creek
                </span>
                <span className="font-heading text-sm uppercase tracking-[0.15em]" style={{ color: draft.header_text_color }}>
                  Hats · Tops · About
                </span>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="announcement" className="mt-6 space-y-4">
            <Row label="Announcement bar enabled">
              <Switch checked={draft.announcement_enabled} onCheckedChange={(v) => setDraft({ ...draft, announcement_enabled: v })} />
            </Row>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-baseline justify-between mb-3">
                <div className="font-ui text-sm font-semibold text-primary">Messages</div>
                <div className="text-xs text-muted-foreground">Rotates every 5 seconds when more than one</div>
              </div>
              <div className="space-y-2">
                {(draft.announcements.length ? draft.announcements : [draft.announcement_text]).map((msg, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <textarea
                      value={msg}
                      onChange={(e) => {
                        const list = [...(draft.announcements.length ? draft.announcements : [draft.announcement_text])];
                        list[i] = e.target.value;
                        setDraft({ ...draft, announcements: list, announcement_text: list[0] ?? "" });
                      }}
                      rows={2}
                      maxLength={200}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const list = (draft.announcements.length ? draft.announcements : [draft.announcement_text]).filter((_, idx) => idx !== i);
                        setDraft({ ...draft, announcements: list, announcement_text: list[0] ?? "" });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  const list = [...(draft.announcements.length ? draft.announcements : [draft.announcement_text]), ""];
                  setDraft({ ...draft, announcements: list });
                }}
              >
                Add announcement
              </Button>
            </div>

            <ColorPicker
              label="Announcement bar color"
              value={draft.announcement_bg_color}
              onChange={(v) => setDraft({ ...draft, announcement_bg_color: v })}
            />
            <ColorPicker
              label="Announcement text color"
              value={draft.announcement_text_color}
              onChange={(v) => setDraft({ ...draft, announcement_text_color: v })}
            />

            <div className="rounded-lg border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview</div>
              <div className="rounded-md py-2 text-center" style={{ backgroundColor: draft.announcement_bg_color }}>
                <span
                  className="font-ui text-xs font-semibold uppercase tracking-[0.2em]"
                  style={{ color: draft.announcement_text_color }}
                >
                  {(draft.announcements[0] || draft.announcement_text) ?? ""}
                </span>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="promo" className="mt-6 space-y-4">
            <Row label="Show 'Buy 2 Get 1 Free' tag on products" description="Hides the corner tag on all product cards when off.">
              <Switch checked={draft.promo_buy2get1_enabled} onCheckedChange={(v) => setDraft({ ...draft, promo_buy2get1_enabled: v })} />
            </Row>
            <div className="rounded-lg border border-border bg-card p-4">
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Marquee text</label>
              <input
                type="text"
                value={draft.promo_marquee_text}
                onChange={(e) => setDraft({ ...draft, promo_marquee_text: e.target.value.slice(0, 80) })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 font-heading text-lg tracking-widest text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
              />
            </div>
            <ColorPicker
              label="Marquee background color"
              value={draft.promo_marquee_bg_color}
              onChange={(v) => setDraft({ ...draft, promo_marquee_bg_color: v })}
            />
            <ColorPicker
              label="Marquee text color"
              value={draft.promo_marquee_text_color}
              onChange={(v) => setDraft({ ...draft, promo_marquee_text_color: v })}
            />
            <div className="rounded-lg border border-border p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview</div>
              <div className="rounded-md px-6 py-4 text-center font-heading text-lg uppercase tracking-[0.15em]" style={{ backgroundColor: draft.promo_marquee_bg_color, color: draft.promo_marquee_text_color }}>
                {draft.promo_marquee_text} ◆ {draft.promo_marquee_text} ◆ {draft.promo_marquee_text}
              </div>
            </div>

            <div className="border-t border-border pt-6 mt-6">
              <h3 className="font-ui text-sm font-semibold text-primary mb-1">Feature marquees</h3>
              <p className="text-xs text-muted-foreground mb-4">Controls the scrolling strips on the home page, The Standard, and Our Values pages.</p>
              <div className="space-y-4">
                <ColorPicker
                  label="Feature marquee background color"
                  value={draft.feature_marquee_bg_color}
                  onChange={(v) => setDraft({ ...draft, feature_marquee_bg_color: v })}
                />
                <ColorPicker
                  label="Feature marquee text color"
                  value={draft.feature_marquee_text_color}
                  onChange={(v) => setDraft({ ...draft, feature_marquee_text_color: v })}
                />
                <div className="rounded-lg border border-border p-4">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview</div>
                  <div className="rounded-md px-6 py-4 text-center font-ui text-xs font-bold uppercase tracking-[0.25em]" style={{ backgroundColor: draft.feature_marquee_bg_color, color: draft.feature_marquee_text_color }}>
                    Premium Craftsmanship ◆ Texas Roots ◆ Field Tested ◆ Dadgum Best
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="popup" className="mt-6 space-y-4">
            <Row label="Captcha popup enabled" description="Shows a hat-finding captcha after 7 seconds on the visitor's first session, then captures their email for the deal.">
              <Switch checked={draft.promo_captcha_enabled} onCheckedChange={(v) => setDraft({ ...draft, promo_captcha_enabled: v })} />
            </Row>
            <div className="rounded-lg border border-border bg-card p-4">
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Discount code shown after signup</label>
              <input
                type="text"
                value={draft.promo_captcha_code}
                onChange={(e) => setDraft({ ...draft, promo_captcha_code: e.target.value.toUpperCase().slice(0, 40) })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 font-heading text-lg tracking-widest text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
              />
            </div>
            <PromoSignupsList />
          </TabsContent>
          <TabsContent value="reviews" className="mt-6 space-y-4">
            <ReviewsTab accessToken={session?.access_token ?? ""} />
          </TabsContent>
        </Tabs>

        <div className="mt-10 flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg" className="bg-blaze text-[#F5EFE2] hover:bg-blaze/90">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewsTab({ accessToken }: { accessToken: string }) {
  const queryClient = useQueryClient();
  const fetchPending = useServerFn(getPendingReviews);
  const moderate = useServerFn(moderateReview);
  const { data, isLoading, error } = useQuery({
    queryKey: ["pending-reviews"],
    queryFn: () => fetchPending({ data: { accessToken } }),
    staleTime: 0,
  });

  const handleModerate = async (id: number, curated: "ok" | "spam") => {
    const result = await moderate({ data: { accessToken, id, curated } });
    if (result.ok) {
      toast.success(curated === "ok" ? "Review published" : "Review hidden");
      queryClient.invalidateQueries({ queryKey: ["pending-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["product-ratings"] });
    } else {
      toast.error(result.error || "Action failed");
    }
  };

  if (data?.enabled === false) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        Judge.me isn't configured yet. Install Judge.me on Shopify and add the JUDGE_ME_API_TOKEN secret to enable review moderation.
      </div>
    );
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading pending reviews…</p>;
  if (error) return <p className="text-sm text-destructive">{error.message}</p>;
  const reviews: PublicReview[] = data?.reviews ?? [];
  if (reviews.length === 0) {
    return <p className="text-sm text-muted-foreground">No pending reviews. New reviews awaiting approval will appear here.</p>;
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{reviews.length} pending review{reviews.length === 1 ? "" : "s"} awaiting approval.</p>
      {reviews.map((r) => (
        <div key={r.id} className="rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-ui text-xs font-bold uppercase tracking-widest text-primary">{r.rating}★</span>
            <span className="font-ui text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString()}</span>
            {r.verified && <span className="font-ui text-[10px] uppercase tracking-widest text-blaze">Verified</span>}
          </div>
          {r.title && <h3 className="mt-2 font-heading text-base uppercase text-primary">{r.title}</h3>}
          <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
          <p className="mt-2 font-ui text-[11px] uppercase tracking-widest text-muted-foreground">— {r.reviewerName}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" className="bg-blaze text-[#F5EFE2] hover:bg-blaze/90" onClick={() => handleModerate(r.id, "ok")}>
              Publish
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleModerate(r.id, "spam")}>
              Hide
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
      <div>
        <div className="font-ui text-sm font-semibold text-primary">{label}</div>
        {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
      </div>
      {children}
    </div>
  );
}

function PromoSignupsList() {
  const [rows, setRows] = useState<Array<{ id: string; email: string; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from("promo_signups")
        .select("id, email, created_at")
        .order("created_at", { ascending: false })
        .limit(50);
      if (!active) return;
      if (error) setErr(error.message);
      else setRows(data ?? []);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-ui text-sm font-semibold text-primary">Recent signups</div>
        <div className="text-xs text-muted-foreground">{rows.length} shown</div>
      </div>
      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
      {!loading && !err && rows.length === 0 && (
        <div className="text-xs text-muted-foreground">No signups yet.</div>
      )}
      {!loading && rows.length > 0 && (
        <ul className="divide-y divide-border max-h-72 overflow-auto">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2 text-sm">
              <span className="text-primary">{r.email}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(r.created_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ColorPicker({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="font-ui text-sm font-semibold text-primary mb-3">{label}</div>
      <div className="flex flex-wrap gap-3">
        {BRAND_COLORS.map((c) => {
          const selected = value.toLowerCase() === c.value.toLowerCase();
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => onChange(c.value)}
              className={`group flex flex-col items-center gap-1.5 rounded-md p-2 transition ${selected ? "ring-2 ring-blaze" : "hover:bg-muted"}`}
              aria-label={c.name}
            >
              <span
                className="h-10 w-10 rounded-full border border-border shadow-sm"
                style={{ backgroundColor: c.value }}
              />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.name}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-9 w-12 cursor-pointer rounded border border-border bg-background"
          aria-label={`${label} custom color`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          className="w-32 rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
        />
      </div>
    </div>
  );
}
